import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import LinkButton from './LinkButton';
import axios from 'axios';
import md5 from 'md5';
import Background from './Background';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentSlash, faCaretRight, faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import Button from './Button';
import ButtonSec from './ButtonSec';
import Menu from './Menu';
import FooterList from './FooterList';
import Seo from './Seo';  // important!

const ComicDetail = ({ onBack }) => {
  const { comicId } = useParams();
  const [comic, setComic] = useState(null);
  const [series, setSeries] = useState(null);
  const [characters, setCharacters] = useState(null);
  const [copyright, setCopyright] = useState('');

  const addResourceId = resource => {
    const regex = /\/(\d+)$/;
    const match = resource.resourceURI.match(regex);
    const resourceId = match ? match[1] : null;
  
    return {
      ...resource,
      resourceId
    };
  };

  const extractResourceId = resourceURI => {
    const regex = /\/(\d+)$/;
    const match = resourceURI.match(regex);
    return match ? match[1] : null;
  };


  useEffect(() => {
    const fetchComic = async () => {
      try {
        const publicKey = process.env.REACT_APP_MARVEL_PUBLIC_KEY;
        const privateKey = process.env.REACT_APP_MARVEL_PRIVATE_KEY;
        

        const timestamp = new Date().getTime();
        const hash = md5(`${timestamp}${privateKey}${publicKey}`);

        const comicEndpoint = `https://gateway.marvel.com/v1/public/comics/${comicId}?apikey=${publicKey}&ts=${timestamp}&hash=${hash}`;
        const charactersEndpoint = `https://gateway.marvel.com/v1/public/comics/${comicId}/characters?apikey=${publicKey}&ts=${timestamp}&hash=${hash}`;

        const [comicResponse, charactersResponse] = await Promise.all([
          axios.get(comicEndpoint),
          axios.get(charactersEndpoint),
        ]);

        const seriesId = extractResourceId(comicResponse.data.data.results[0].series.resourceURI);
        const seriesEndpoint = `https://gateway.marvel.com/v1/public/series/${seriesId}?apikey=${publicKey}&ts=${timestamp}&hash=${hash}`;

        const [serieResponse] = await Promise.all([
          axios.get(seriesEndpoint),
        ]);
        
        const comicData = comicResponse.data.data.results[0];
        const seriesData = serieResponse.data.data.results;
        const charactersData = charactersResponse.data.data.results;
        const copyrightText = comicResponse.data.attributionText;

        setCopyright(copyrightText);
        setSeries(seriesData);
        setComic(comicData);
        setCharacters(charactersData);
      } catch (error) {
        console.error('Error fetching comic:', error);
      }
    };

    fetchComic();
  }, [comicId]);


  if (!comic) {
    return (
    <Background>
      <div className='flex flex-col'>
        <img className='w-48 items-center' src="/images/logo/loading-29.gif" alt="loading-29"/>
        <FooterList textVariable={copyright} />
      </div>
    </Background>
    );
  }

  return (
  <Background>
    <div className='flex flex-col'>
    <Seo 
      title={comic.title} 
      description={`Comic info - ${comic.title}`} 
      type='article'
      image_link={`${comic.thumbnail?.path}/portrait_uncanny.${comic.thumbnail?.extension}`}
      keywords='mv-app, mvapp'
      url=''
    />
    <Menu textVariable='Comic detail' />

    <div className='flex flex-col md:flex-row lg:flex-row bg-gradient-to-r from-red-600 via-red-700 to-red-500 hover:shadow-xl hover:shadow-red-400/70 m-2'>
      <div className='w-3/3 md:w-1/3 m-4'>
        <img 
          className='w-full shadow-md' 
          src={
            comic.thumbnail.path && comic.thumbnail.path !== "http://i.annihil.us/u/prod/marvel/i/mg/b/40/image_not_available"
              ? `${comic.thumbnail.path}/portrait_uncanny.${comic.thumbnail.extension}`
              : '/images/image_not_set.jpg'
          } 
          alt={comic.name}
        />
      </div>
{/*  */}
      <div className='w-3/3 md:w-2/3 m-4'>
        <p className='text-2xl md:text-2xl lg:text-3xl text-slate-50 m-2'><FontAwesomeIcon icon={faCaretRight}/>&emsp;{comic.title || 'Comic Title Not Available'}</p>
        <p className='text-2xl text-slate-50 shadow-md'>Description comic:</p>
          <p className='text-xl text-slate-100'>{comic.description ? (comic.description) : (<span className='text-xl'><FontAwesomeIcon icon={faCommentSlash}/>&emsp;No description available</span>)}</p>
          <p className="text-l text-slate-50">{comic.pageCount > 0 ? `Page count: ${comic.pageCount}` : ''}</p>

        {comic.textObjects.length > 0 ? (
        <div className='mb-2'>
            <p className='text-l md:text-xl lg:text-xl text-slate-50 shadow-md'>More description:</p>
            <ul>
            {comic.textObjects.length > 0 ? (
                comic.textObjects.map((textObjects) => {
                return (
                  <li className='text-l text-slate-100 ' key={textObjects.text}>
                  <FontAwesomeIcon icon={faCaretRight} />&emsp;{textObjects.text}
                  </li>
                );
              })
            ) : (
              <li className='text-l text-slate-100'><FontAwesomeIcon icon={faCircleQuestion} />&emsp;No descriptions</li>
            )}
            </ul>
        </div>
        ) : (
          null
        )}
        {/*  */}
        <div className='w-full mb-2'>
            <p className='text-2xl text-slate-50 shadow-md mb-2'>Characters:</p>
            <ul>
            {comic.characters.items.length > 0 ? (
                comic.characters.items.map((character) => {
                const characterWithId = addResourceId(character);
                return (
                    <li className='text-l text-slate-100 ml-2' key={character.resourceURI}>
                    <LinkButton to={`/characters/${characterWithId.resourceId}`}><Button>Character info</Button></LinkButton>&emsp;
                    <FontAwesomeIcon icon={faCaretRight} />&emsp;{character.name}
                    </li>
                );
                })
            ) : (
                <li className='text-l text-slate-100 ml-2'><FontAwesomeIcon icon={faCircleQuestion} />&emsp;No characters</li>
            )}
            </ul>
        </div>
        {/*  */}
        <div className='w-full mb-2'>
            <p className='text-2xl text-slate-50 shadow-md mb-2'>Creators:</p>
            <ul>
            {comic.creators.items.length > 0 ? (
                comic.creators.items.map((creators) => {
                const creatorsWithId = addResourceId(creators);
                return (
                    <li className='text-l text-slate-100 truncate ... ml-2' key={creators.resourceURI}>
                    <LinkButton to={`/creator/${creatorsWithId.resourceId}`}><Button>Creator info</Button></LinkButton>&emsp;
                    <FontAwesomeIcon icon={faCaretRight} />&emsp;{creators.name} - {creators.role}
                    </li>
                );
                })
            ) : (
                <li className='text-l text-slate-100 ml-2'><FontAwesomeIcon icon={faCircleQuestion} />&emsp;No creators</li>
            )}
            </ul>
        </div>
{/*  */}
        <div className='w-full mb-2'>
          <p className='text-2xl text-slate-50 shadow-md mb-2'>Serie:</p>
          <ul>
          {comic.series && comic.series.resourceURI ? (
            <li className='text-l text-slate-100 ml-2 truncate' key={comic.series.resourceURI}>
              <LinkButton to={`/serie/${extractResourceId(comic.series.resourceURI)}`}>
                <Button>Series info</Button>
              </LinkButton>&emsp;
              <FontAwesomeIcon icon={faCaretRight}/>&emsp;{comic.series.name}
            </li>
          ) : (
            <li className='text-l text-slate-100 ml-2'>No series information available</li>
          )}
          </ul>
        </div>

        </div>
      </div>
{/*  */}

      {characters.length > 0 ? (
      <div className='flex bg-gradient-to-br from-red-500 via-red-700 to-red-400 m-2'>
        <div className='w-full text-2xl md:text-2xl lg:text-2xl text-slate-50 m-4'>
          All comic characters
        </div>
      </div>
      ) : (
        null
      )}

      {characters.length > 0 ? (
        <div className='grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6'>
        {characters.map((character) => (      
          <div key={character.id}  className='flex flex-col justify-between bg-gradient-to-r from-red-600 via-red-700 to-red-500 hover:shadow-xl hover:shadow-red-400/70 m-2'>
            <div className="m-4">
            <Link to={`/characters/${character.id}`} onClick={() => window.scrollTo(0, 0)}>
            <img 
            className='w-full shadow-md hover:shadow-xl hover:shadow-red-400/70 transform hover:scale-95 transition-transform duration-200' 
            src={
              character.thumbnail.path && character.thumbnail.path !== "http://i.annihil.us/u/prod/marvel/i/mg/b/40/image_not_available"
                ? `${character.thumbnail.path}/portrait_uncanny.${character.thumbnail.extension}`
                : '/images/image_empty.jpg'
            } 
            alt={character.name} 
          /> 
            </Link>
            </div>
            <div className='m-4'>              
              <Link to={`/characters/${character.id}`}><ButtonSec size="text-l" fullWidth={true}>{character.name}</ButtonSec></Link>
            </div>
          </div>
        ))}
        </div>
        ) : (
          null
        )}

      {series.length > 0 ? (
      <div className='flex bg-gradient-to-br from-red-500 via-red-700 to-red-400 m-2'>
        <div className='w-full text-2xl md:text-2xl lg:text-2xl text-slate-50 m-4'>
          The complete {series.title} comic book series
        </div>
      </div>
      ) : (
        null
      )}
      {series.length > 0 ? (
      <div className='flex flex-col md:flex-row lg:flex-row bg-gradient-to-r from-red-600 via-red-700 to-red-500 hover:shadow-xl hover:shadow-red-400/70 m-2'>
        <div className='w-5/5 md:w-1/5 lg:w-1/5 m-4'>
          <Link to={`/serie/${series[0].id}`} onClick={() => window.scrollTo(0, 0)}>
          {/* <img className='w-full md:h-full lg:h-full shadow-md hover:shadow-xl hover:shadow-red-400/70 transform hover:scale-95 transition-transform duration-200' src={`${series[0].thumbnail.path}/portrait_uncanny.${series[0].thumbnail.extension}`} alt={series[0].name} /> */}
          <img 
            className='w-full md:h-full lg:h-full shadow-md hover:shadow-xl hover:shadow-red-400/70 transform hover:scale-95 transition-transform duration-200' 
            src={
              series[0].thumbnail.path && series[0].thumbnail.path !== "http://i.annihil.us/u/prod/marvel/i/mg/b/40/image_not_available"
                ? `${series[0].thumbnail.path}/portrait_uncanny.${series[0].thumbnail.extension}`
                : '/images/image_empty.jpg'
            } 
            alt={series[0].name} 
          />       
          </Link>
        </div>
        <div className='w-5/5 md:w-4/5 lg:w-4/5 m-4'>
        <p className='text-l md:text-2xl lg:text-2xl text-slate-50'>Series&emsp;<FontAwesomeIcon icon={faCaretRight}/>&emsp;<Link to={`/serie/${series[0].id}`} onClick={() => window.scrollTo(0, 0)} className='text-l md:text-2xl lg:text-2xl text-slate-50 underline decoration-red-300 hover:decoration-red-100 hover:text-red-200'>{series[0].title}</Link></p>
        <p className='text-l md:text-xl lg:text-l text-slate-100'>{series[0].description}</p>
        </div>
      </div>
      ) : (
        null
      )}

      <FooterList textVariable={copyright} />
    
    </div>

  </Background>
  );
};

export default ComicDetail;
